import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  Link,
  Grid,
  useTheme,
  IconButton,
  Stack,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  Web as WebIcon,
  Map as MapIcon,
  Campaign as CampaignIcon,
  Brush as BrushIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import EnquiryDialog from "./EnquiryDialog";

const SERVICES = [
  {
    icon: WebIcon,
    title: "Web Design",
    desc: "Craft visually stunning, user-friendly websites that captivate and convert your audience.",
    color: "primary",
  },
  {
    icon: MapIcon,
    title: "GIS Services",
    desc: "Leverage geographic data to drive better decision-making with our cutting-edge GIS solutions.",
    color: "secondary",
  },
  {
    icon: CampaignIcon,
    title: "Digital Marketing",
    desc: "Empower your brand's online presence with data-driven digital marketing strategies.",
    color: "info",
  },
  {
    icon: BrushIcon,
    title: "Art Sales",
    desc: "Connect artists with collectors, creating a thriving marketplace for unique, inspiring art.",
    color: "error",
  },
];

export default function Services() {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6 },
        backgroundColor: "background.default",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: (theme) =>
            `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, transparent)`,
        },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={6}>
          <Stack
            spacing={2}
            sx={{
              textAlign: "center",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mb: 1,
                textAlign: "center",
              }}
              data-aos="fade-up"
            >
              Our Services
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center" }}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Comprehensive solutions tailored to elevate your business in the
              digital age
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {SERVICES.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={service.title}>
                <ServiceCard {...service} index={index} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

function ServiceCard({ icon: Icon, title, desc, color, index }) {
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();

  return (
    <>
      <Card
        elevation={0}
        data-aos="fade-up"
        data-aos-delay={100 * index}
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 4,
          transition: "all 0.3s ease-in-out",
          border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: theme.shadows[8],
            borderColor: "transparent",
            bgcolor: (theme) =>
              `linear-gradient(135deg, ${alpha(
                theme.palette[color].main,
                0.05
              )}, ${alpha(theme.palette[color].main, 0.1)})`,
          },
        }}
      >
        <Stack spacing={3}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
            }}
          >
            <Icon
              sx={{
                fontSize: 32,
                color: `${color}.main`,
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.primary",
                minHeight: 64,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, minHeight: 72 }}
            >
              {desc}
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: "auto" }}
          >
            <Tooltip title="Learn More">
              <IconButton
                onClick={() => setOpenDialog(true)}
                size="small"
                sx={{
                  color: `${color}.main`,
                  bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette[color].main, 0.2),
                  },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Tooltip>
            <Link
              component="button"
              onClick={() => setOpenDialog(true)}
              underline="hover"
              sx={{
                color: `${color}.main`,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Learn More
            </Link>
          </Stack>
        </Stack>
      </Card>

      <EnquiryDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={title}
      />
    </>
  );
}
